import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-final-form';
import pick from 'lodash/pick';

// Constants
import PageUrls from 'Shared/constants/PageUrls.constants';

// Hooks
import { useResponsiveScreenWidth } from 'Wpds/hooks/useResponsiveScreenWidth';
import { useQueryParam } from 'Shared/utils/hooks';

// Actions
import { useInviteToWorkspace } from 'Shared/actions/inviteToWorkspace';
import { createGoalV2, updateGoal, deleteGoal } from 'Shared/actions/Goals.actions';

// Components
import { WpPageLayout, WpPageHeader, WpPageTitle, WpPageBody } from 'Wpds/components/page-layout';
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import { WpSteps, WpStep } from 'Wpds/components/steps-v2';
import PrivacyAndRemindersV2 from './PrivacyAndReminders/PrivacyAndRemindersV2';
import NameAndDescription from './NameAndDescription/NameAndDescription';
import EditGoalButtonsArea from './GoalsButtonsArea/EditGoalButtonArea';
import NewGoalButtonsArea from './GoalsButtonsArea/NewGoalButtonsArea';
// import GoalsPageHeader from './GoalsPageHeader/GoalsPageHeader';
import GoalTracking from './GoalTracking/GoalTracking';

// Utils
import { generateGoalV2 } from './utils';
import { showNotificationError } from 'Shared/helpers';

// Constants
import { GoalWhiteList } from './constants';
import { ReminderType, Steps } from './types';
import { GoalItem, GoalIcons } from 'Shared/constants/types';
import TrackingConstants from 'Shared/constants/Tracking.constants';

import './CreateOrUpdateGoalPage.scss';

type Props = {
  goal?: GoalItem;
  toggleDisplay?: Function;
  isEdit?: boolean;
  handleRedirectBack?: Function;
};

function CreateOrUpdateGoalPageV2({ goal, isEdit, toggleDisplay, handleRedirectBack }: Props) {
  const history = useHistory();
  const dispatch = useDispatch();
  const [parentId] = useQueryParam('parentId');
  const { isLarge } = useResponsiveScreenWidth({
    isAtLeast: true,
  });

  const workspaces = useSelector(state => state.workspaces);
  const users = useSelector(state => state.members);

  const isCloning = goal && !isEdit;

  const getTitle = () => {
    if (isEdit) return 'Edit Goal';
    if (isCloning) return 'Duplicate Goal';
    return 'Create Goal';
  };

  const [step, setStep] = useState(Steps.nameAndDescription);

  const redirectToGoalDetailsPage = useCallback(
    slug => {
      history.push(`/goals/${slug}`);
    },
    [history]
  );

  const handleGoalDelete = useCallback(() => dispatch(deleteGoal(goal?.slug)).then(handleRedirectBack), [
    dispatch,
    goal,
    handleRedirectBack,
  ]);

  const createNewGoal = useCallback(
    values => {
      return dispatch(createGoalV2({ goal: generateGoalV2(values) })).then(response => {
        const newGoal = response.data.attributes;
        redirectToGoalDetailsPage(newGoal.slug);
        if (toggleDisplay) toggleDisplay();
      });
    },
    [dispatch, redirectToGoalDetailsPage, toggleDisplay]
  );
  const [inviteToWorkspace] = useInviteToWorkspace({
    inviteMethod: TrackingConstants.INVITE_METHOD_TYPES.CREATE_UPDATE_GOAL,
  });
  const handleAddUserToWorkspace = async (assigneeId, workspaceId) => {
    const assignee = users.find(u => u.id === assigneeId);
    try {
      await inviteToWorkspace({
        workspaceId: workspaceId,
        emails: [assignee.email],
      });
    } catch (e) {
      showNotificationError(e);
    }
  };

  const inviteAssigneeToGroupWorkspace = useCallback(
    ({ workspaceId, assigneeId }) => {
      const groupWorkspace = workspaces.find(({ id }) => workspaceId === id);
      const groupWorkspaceUsers = groupWorkspace.users;
      const assigneeUser = groupWorkspaceUsers.find(({ id }) => assigneeId === id);
      if (assigneeUser) return false;
      handleAddUserToWorkspace(assigneeId, workspaceId);
      return true;
    },
    [workspaces]
  );

  const handleNextStep = useCallback(
    values => {
      if (step === Steps.nameAndDescription && isEdit) {
        if (inviteAssigneeToGroupWorkspace(values)) return null;
        return dispatch(updateGoal({ goal: generateGoalV2(values) })).then(() => toggleDisplay());
      }
      if (step === Steps.nameAndDescription) {
        return setStep(Steps.privacyAndReminders);
      }
      if (step === Steps.privacyAndReminders) {
        inviteAssigneeToGroupWorkspace(values);
        return createNewGoal(values);
      }
      return values;
    },
    [step, isEdit, inviteAssigneeToGroupWorkspace, dispatch, toggleDisplay, createNewGoal]
  );

  const handleBackButtonClick = useCallback(() => {
    if (step === Steps.privacyAndReminders) setStep(Steps.nameAndDescription);
    if (step === Steps.nameAndDescription && history.length > 0) history.goBack();
    if (step === Steps.nameAndDescription && history.length === 0) history.push(PageUrls.HOME_PAGE);
  }, [step, history, setStep]);

  const initialValues = {
    ...pick(
      {
        ...goal,
        assigneeId: goal?.assigneeId,
        parentId: goal?.parentId || (parentId ? Number(parentId) : null),
        description: goal?.description || '',
        icon: goal?.icon || GoalIcons.ice_cream,
        privacy: goal ? (goal.private ? 'private' : 'public') : 'public',
        workspaceId: goal?.workspaceSlug,
      },
      GoalWhiteList
    ),
    reminder: ReminderType[goal?.reminderSetting] || ReminderType.onceAWeek,
  };

  const baseClassName = 'CreateOrUpdateGoalPageV2';

  return (
    <WpPageLayout className={baseClassName} width="container">
      <WpPageHeader sticky slim>
        <WpPageTitle title={getTitle()} />
      </WpPageHeader>
      <WpPageBody>
        <div className={`${baseClassName}__content`}>
          {!isEdit && (
            <div className={`${baseClassName}__content__steps`}>
              <div className={`${baseClassName}__content__steps__inner`}>
                <WpSteps
                  direction={isLarge ? 'vertical' : 'horizontal'}
                  activeStepNumber={step === Steps.privacyAndReminders ? 2 : 1}
                >
                  <WpStep title="Name & Description" />
                  <WpStep title="Privacy & Reminders" />
                </WpSteps>
              </div>
            </div>
          )}
          <WpContainer className={`${baseClassName}__content__container`}>
            <WpContainerBody>
              <Form initialValues={initialValues} onSubmit={handleNextStep}>
                {({ handleSubmit }) => (
                  <form onSubmit={handleSubmit}>
                    {/* <GoalsPageHeader isClone={isCloning} isEdit={isEdit} step={step} /> */}
                    <div>
                      {isEdit || step === Steps.nameAndDescription ? (
                        <>
                          <NameAndDescription isEdit={isEdit} />
                          <GoalTracking />
                        </>
                      ) : null}
                      {isEdit || step === Steps.privacyAndReminders ? (
                        <PrivacyAndRemindersV2 workspaceSlug={goal?.workspaceSlug} />
                      ) : null}
                      {isEdit ? (
                        <EditGoalButtonsArea onDelete={handleGoalDelete} onCancel={toggleDisplay} />
                      ) : (
                        <NewGoalButtonsArea step={step} onBack={handleBackButtonClick} onCancel={toggleDisplay} />
                      )}
                    </div>
                  </form>
                )}
              </Form>
            </WpContainerBody>
          </WpContainer>
        </div>
      </WpPageBody>
    </WpPageLayout>
  );
}

export default CreateOrUpdateGoalPageV2;
